/**
 * Expenses — the Reimburse phone app's reports, at the desk.
 *
 * Same records as the phone: a report made here shows up there, and a receipt
 * snapped there shows up here. Three tabs: the open reports, the invoiced ones,
 * and the Quicken worklist of everything not yet recorded.
 *
 * Categories and mileage rates are under Settings, with the rest of the app's
 * configuration.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { card } from "@/components/atoms";
import { useChrome } from "@/context/ChromeContext";
import { useToast } from "@/context/ToastContext";
import { useProjects } from "@/hooks/useData";
import {
  downloadCsv, useExpenseCategories, useExpenseReports, useExpenses,
} from "@/hooks/useExpenses";
import { useClients } from "@/hooks/useTime";
import {
  allExpensesCsvRows, byReport, categoryNamer, fmtInstant, fmtPeriod, isInvoiced, money, toCsv, totalsFrom,
} from "@/lib/expenses";
import { useIsMobile } from "@/lib/useMediaQuery";

import { Quicken } from "./expenses/Quicken";
import { ReportForm } from "./expenses/ReportForm";
import { HeaderTabs, ghostBtn, primaryBtn, td, th } from "./expenses/ui";

type Tab = "active" | "invoiced" | "quicken";
const TABS: [Tab, string][] = [["active", "Active"], ["invoiced", "Invoiced"], ["quicken", "Quicken"]];

export function Expenses() {
  const mobile = useIsMobile();
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const asked = params.get("tab") as Tab | null;
  const [tab, setTab] = useState<Tab>(asked && TABS.some(([k]) => k === asked) ? asked : "active");
  useEffect(() => {
    if (params.get("tab")) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: reports = [], isLoading } = useExpenseReports();
  const { data: expenses = [] } = useExpenses();
  const { data: cats = [] } = useExpenseCategories();
  const { data: clients = [] } = useClients();
  const { data: projects = [] } = useProjects();
  const [creating, setCreating] = useState(false);

  const catName = useMemo(() => categoryNamer(cats), [cats]);
  const grouped = useMemo(() => byReport(expenses), [expenses]);
  const clientName = (id: string) => clients.find((c) => c.id === id)?.name ?? "";
  const projectName = (id?: string) => (id && projects.find((p) => p.id === id)?.name) || "";

  const shown = reports
    .filter((r) => (tab === "invoiced" ? isInvoiced(r.status) : !isInvoiced(r.status)))
    .map((r) => ({ r, t: totalsFrom(grouped.get(r.id) ?? [], catName) }));
  const grand = shown.reduce((s, x) => s + x.t.total, 0);
  const needs = tab === "active" ? shown.filter((x) => x.t.missing > 0 || x.t.unpriced > 0) : [];

  function exportAll() {
    const rows = allExpensesCsvRows(reports.map((r) => ({ id: r.id, name: r.name, clientName: clientName(r.client) })), expenses, catName);
    downloadCsv(toCsv(rows), "reimbursed_all.csv");
    toast(`Exported ${rows.length - 1} expense${rows.length === 2 ? "" : "s"}`);
  }

  useChrome({
    title: "Expenses",
    subtitle: tab === "quicken" ? "What still needs recording in Quicken" : "Reimbursement reports — shared with the Reimburse app",
    tabs: <HeaderTabs value={tab} options={TABS} onChange={setTab} />,
    actions: (
      <>
        <button onClick={exportAll} style={ghostBtn} title="Every expense in every report, as one CSV">Export all</button>
        <button onClick={() => setCreating(true)} style={primaryBtn}>New report</button>
      </>
    ),
  }, [tab]);

  return (
    <div style={{ padding: mobile ? "16px 16px 90px" : "20px 36px 90px", maxWidth: 1100 }}>
      {tab === "quicken" ? <Quicken /> : (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
                {tab === "invoiced"
                  ? `Invoiced · ${shown.length} report${shown.length === 1 ? "" : "s"}`
                  : `Unreimbursed · ${shown.length} open report${shown.length === 1 ? "" : "s"}`}
              </div>
              <div className="mono" style={{ fontSize: 30, fontWeight: 500, letterSpacing: "-0.02em" }}>{money(grand)}</div>
            </div>
          </div>

          {needs.length > 0 && (
            <div style={{ ...card, padding: "12px 16px", marginBottom: 14, background: "var(--warnTint)", border: "1px solid var(--warnLine)" }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Needs attention</div>
              {needs.map(({ r, t }) => (
                <div key={r.id} style={{ fontSize: 12.5, color: "var(--ink2)", padding: "2px 0" }}>
                  <Link to={`/expenses/${r.id}`} style={{ color: "inherit" }}>{r.name}</Link>
                  {" — "}{[
                    t.unpriced > 0 && `${t.unpriced} item${t.unpriced === 1 ? "" : "s"} with no amount yet`,
                    t.missing > 0 && `${t.missing} receipt${t.missing === 1 ? "" : "s"} to capture`,
                  ].filter(Boolean).join(" · ")}
                </div>
              ))}
            </div>
          )}

          <div style={{ ...card, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Report</th>
                  <th style={th}>Client · project</th>
                  <th style={th}>Period</th>
                  <th style={{ ...th, textAlign: "right" }}>Items</th>
                  <th style={th}>Status</th>
                  <th style={{ ...th, textAlign: "right" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {shown.map(({ r, t }) => (
                  // The whole row opens the report; the name stays a real link
                  // for keyboard use and opening in a new tab.
                  <tr key={r.id} className="expense-report-row" onClick={() => navigate(`/expenses/${r.id}`)} style={{ cursor: "pointer" }}>
                    <td style={td}>
                      <Link to={`/expenses/${r.id}`} onClick={(e) => e.stopPropagation()}
                        style={{ color: "var(--accent)", fontWeight: 500, textDecoration: "none" }}>{r.name}</Link>
                    </td>
                    <td style={{ ...td, color: "var(--ink3)" }}>{[clientName(r.client), projectName(r.project)].filter(Boolean).join(" · ")}</td>
                    <td style={{ ...td, color: "var(--ink3)", whiteSpace: "nowrap" }}>{fmtPeriod(r.start_date, r.end_date)}</td>
                    <td style={{ ...td, textAlign: "right" }} className="mono">{t.count}</td>
                    <td style={{ ...td, whiteSpace: "nowrap", fontSize: 12 }}>
                      {tab === "invoiced"
                        ? <span style={{ color: "var(--good)" }}>Invoiced{r.invoiced_at ? ` ${fmtInstant(r.invoiced_at)}` : ""}</span>
                        : t.unpriced
                          ? <span style={{ color: "var(--warn)" }}>{t.unpriced} to finish</span>
                          : t.missing
                          ? <span style={{ color: "var(--warn)" }}>{t.missing} receipt{t.missing === 1 ? "" : "s"} to capture</span>
                          : <span style={{ color: "var(--good)" }}>Ready to invoice</span>}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 500 }} className="mono">{money(t.total)}</td>
                  </tr>
                ))}
                {shown.length === 0 && (
                  <tr><td colSpan={6} style={{ ...td, color: "var(--muted)", textAlign: "center", padding: 28 }}>
                    {isLoading ? "Loading…" : tab === "invoiced" ? "Nothing invoiced yet." : "No open reports. Start one with New report."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {creating && <ReportForm onClose={() => setCreating(false)} />}
    </div>
  );
}
